<script setup lang="ts">
import { computed } from 'vue';
import LiveTrackCard from '@/components/LiveTrackCard.vue';
import CardTags from '@/components/CardTags.vue';
import CoverArtTile from '@/components/CoverArtTile.vue';
// Lazy like LazyTooltip below, and for the same reason: one per line × two
// lines × a bucket's 500 rows. Nothing but a truncated span is mounted until a
// line is hovered AND is actually clipped.
import OverflowTooltip from '@/components/OverflowTooltip.vue';
import TrackRatingBadge from '@/components/AudioFileIssues/TrackRatingBadge.vue';
import TrackVoteIndicator from '@/components/TrackVotes/TrackVoteIndicator.vue';
// Lazy, not eager: `full` renders four tooltips per card and a bucket resolves
// 500 tracks, so mounting them up-front is what locked the page up on a density
// switch. See LazyTooltip's docblock.
import { LazyTooltip, TooltipRow } from '@/components/ui/tooltip';
import { CalendarPlus, Upload } from 'lucide-vue-next';
import { formatUsMmDdYy, formatUsMmDdYyTime12h } from '@/utils/date';
import { useInspectorStore } from '@/stores/inspectorStore';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import { markRaw } from 'vue';
import LiveTrackUsageInspectorContent from '@/components/LiveTrackUsageInspectorContent.vue';
import { trackUsageInspectorTitle } from '@/utils/liveTrackRow';
import type { TrackCardDensity } from '@/composables/trackCardDensity';
import type { PlaylistTrackRow } from '@/composables/api/usePlaylistSwitchingApi';

/**
 * Inspector-style card surface for a single track, at three densities:
 *
 *  - `compact` — a one-line row: small cover, title (remix) over artist — album,
 *    with the playlist position over the duration on the right. Identity only.
 *  - `default` — a card with that same identity (cover, title/remix,
 *    artist — album, position over duration) plus the rating pill, genre, and
 *    tag chips.
 *  - `full` — the forensic card built on {@link LiveTrackCard}: adds the live
 *    track code and playlist count (top-right), ©year, and the bitrate / ISRC /
 *    dates row. This is the original card.
 *
 * At every density the cover art is the play/zoom target, so the text lines
 * navigate: the title opens the Live Track, and the artist and album open their
 * own records. Lines that don't fit ellipsise and open a tooltip with the full
 * text — only when they are actually clipped, so a line that fits stays quiet.
 * Every density shows title (remix) and artist — album; code appears only in
 * `full`.
 */
const props = withDefaults(
  defineProps<{
    track: PlaylistTrackRow;
    density?: TrackCardDensity;
    /**
     * Drop the card's own background and border, so it sits directly on
     * whatever surface hosts it. For places that already draw a container —
     * a player-log entry, for one — where a second opaque surface reads as a
     * separate thing floating inside the first.
     */
    flush?: boolean;
    /**
     * Give the card's click to whatever is hosting it.
     *
     * A card is normally a set of separate targets: the cover plays the track,
     * the title opens the Live Track, the artist and album open their own
     * records. In the QUEUE none of that is what a click means — clicking a row
     * seats the player on that entry — so those targets would each do something
     * other than the one thing the row is for.
     *
     * With this set, the text lines render as plain text and the cover loses its
     * play overlay, leaving the host free to make the whole card one control.
     * The cover keeps its zoom, with the click stopped so it opens the preview
     * without also triggering the host.
     */
    hostHandlesClick?: boolean;
    /**
     * This card is a row of the PLAY QUEUE, and identifies which entry.
     *
     * Keeps the cover's play button — the control people already reach for on
     * every other track surface — and points it at this entry instead of at a
     * list to start. Without it a queue row would be the one place in the app
     * where cover art does not play.
     */
    queueEntryId?: string | null;
    /**
     * Show this number instead of the row's own `position`.
     *
     * A queue row's number is where it sits in the QUEUE, which stops matching
     * the playlist position it was stamped with the moment anything is
     * reordered, removed or shuffled.
     */
    positionOverride?: number | null;
    /**
     * Why this track would not play. Shown as a badge on the cover art rather
     * than as a row element, so a failed card is the same shape as a good one.
     */
    failureReason?: string | null;
  }>(),
  {
    density: 'default',
    flush: false,
    hostHandlesClick: false,
    queueEntryId: null,
    positionOverride: null,
    failureReason: null,
  },
);

function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return '';
  }
  const totalWhole = Math.round(totalSeconds);
  const minutes = Math.floor(totalWhole / 60);
  const seconds = totalWhole % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

const trackTitle = computed(() => {
  const name = props.track.name ?? '';
  const remix = props.track.remix_name ?? '';
  if (!name) return props.track.code ?? 'Untitled track';
  return remix ? `${name} (${remix})` : name;
});

/** The compact/default secondary line, split so each part opens its own record. */
const artistLabel = computed(() => props.track.artist_name?.trim() || 'Unknown artist');
const albumLabel = computed(() => props.track.album_name?.trim() ?? '');
/** Full text for the native tooltip, since the line ellipsises when it doesn't fit. */
const artistAlbumTooltip = computed(() =>
  albumLabel.value ? `${artistLabel.value} — ${albumLabel.value}` : artistLabel.value,
);

const durationLabel = computed(() => formatDuration(props.track.duration));

const inspectorStore = useInspectorStore();
const { canOpen, openRecord } = useOpenRecordInInspector();

/**
 * PlayTrackButton props for the cover overlay (full-bleed play target on the art).
 *
 * A queue row keeps the overlay and points it at its own entry; the button
 * handles that case itself. Only a host that owns the click AND is not a queue
 * drops it, leaving the plain zoomable cover.
 */
const coverPlay = computed(() => (props.hostHandlesClick && props.queueEntryId == null ? undefined : {
  kind: 'live_track' as const,
  queueEntryId: props.queueEntryId,
  liveTrackCode: props.track.code,
  hasAudio: Boolean(props.track.code),
  title: trackTitle.value,
  artist: props.track.artist_name,
  album: props.track.album_name,
  coverUrl: props.track.cover_art_signed_url,
  // The player bar links its three identity lines to these, so the card's
  // navigation stays available once the row has scrolled away.
  liveTrackId: props.track.id,
  artistId: props.track.artist_id,
  albumId: props.track.album_id,
  // A generated playlist can name the same track more than once, so the row's
  // own number is the only thing that says WHICH occurrence was clicked when
  // the player adopts this list as its queue.
  queuePosition: props.track.position ?? null,
  // So a queue seated on this row renders this same card back.
  card: props.track,
}));

/**
 * A line is a link only when this card owns its own clicks AND the record it
 * would open exists. `component :is` rather than two branches so the text and
 * its fallback are written once per line.
 */
const titleTag = computed(() => (props.hostHandlesClick ? 'span' : 'button'));
const artistTag = computed(() =>
  !props.hostHandlesClick && props.track.artist_id != null ? 'button' : 'span',
);
const albumTag = computed(() =>
  !props.hostHandlesClick && props.track.album_id != null ? 'button' : 'span',
);

/** The number this card shows: the queue's, when it has one, else the list's. */
const displayPosition = computed(() => props.positionOverride ?? props.track.position ?? null);

const LINK_AFFORDANCE =
  'hover:text-primary cursor-pointer underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none';

/**
 * The "artist — album" line is a flex row where each part truncates itself.
 *
 * A `<button>` is an atomic inline box — Chrome blockifies `display: inline` on
 * it to `inline-block` — and an ellipsis cannot be put inside a box that cannot
 * be broken. As one plain truncating line, the ellipsis therefore only appeared
 * when the clip happened to land in the text AFTER the artist; a long artist
 * name was sliced mid-word with no ellipsis at all.
 *
 * The album gives up the room first (`shrink-0` on the artist), so a short
 * artist still reads in full with the album ellipsised after it — and an artist
 * too long for the line ellipsises at `max-w-full` instead of being cut. The
 * tooltip carries both either way: `isTextOverflowing` walks descendants for
 * exactly this shape, since a line of self-truncating parts never widens its
 * container and would otherwise measure as fitting.
 */
const artistLineClass = computed(
  () =>
    `shrink-0 max-w-full truncate text-left ${artistTag.value === 'button' ? LINK_AFFORDANCE : ''}`,
);
const albumLineClass = computed(
  () => `min-w-0 truncate text-left ${albumTag.value === 'button' ? LINK_AFFORDANCE : ''}`,
);

/** Link affordances for the title, which is the whole line rather than a word in it. */
const titleLinkClass = computed(() =>
  titleTag.value === 'button'
    ? 'hover:text-primary focus-visible:text-primary cursor-pointer underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none'
    : '',
);
const compactTitleClass = computed(
  () => `text-left text-sm leading-tight font-medium ${titleLinkClass.value}`,
);
const defaultTitleClass = computed(
  () => `text-left text-base leading-tight font-semibold ${titleLinkClass.value}`,
);

/**
 * The cover art is the play target, so the text lines are navigation: the title
 * opens the Live Track, and the artist / album open their own records. These
 * push (not open) when already inside an inspector, so the back chevron returns here.
 */
function openTrackRecord() {
  if (canOpen('LiveTrack')) {
    openRecord('LiveTrack', props.track.id);
  }
}

function openArtistRecord() {
  const artistId = props.track.artist_id;
  if (artistId != null && canOpen('Artist')) {
    openRecord('Artist', artistId);
  }
}

function openAlbumRecord() {
  const albumId = props.track.album_id;
  if (albumId != null && canOpen('Album')) {
    openRecord('Album', albumId);
  }
}

/**
 * Open this track with its Lyrics section selected.
 *
 * Goes through the same record-opening path the title uses, so it lands in
 * whatever inspector surface the card is on, with `initialSection` telling
 * {@link LiveTrackInspector} which of its two sections to show.
 */
function openLyricsView() {
  if (canOpen('LiveTrack')) {
    openRecord('LiveTrack', props.track.id, { initialSection: 'lyrics' });
  }
}

/** TrackCard renders inside an inspector already (Playlists report), so push, not open. */
function openUsageInspector() {
  inspectorStore.push(
    trackUsageInspectorTitle(trackTitle.value),
    markRaw(LiveTrackUsageInspectorContent),
    { liveTrackId: props.track.id },
  );
}

</script>

<template>
  <!-- Compact: one-line identity row. -->
  <div
    v-if="density === 'compact'"
    :class="[
      'flex items-center gap-3',
      flush ? '' : 'border-border/80 bg-card rounded-lg border px-3 py-2',
    ]"
    :data-testid="`track-card-${track.code ?? track.id}`"
  >
    <!-- The cover owns its own clicks (play, expand, zoom) and stops them here,
         so a host that made the whole card one control still gets the cover's
         preview rather than its own action. -->
    <span class="shrink-0" @click.stop>
      <CoverArtTile
        :src="track.cover_art_signed_url ?? null"
        :alt="`Cover art for ${trackTitle}`"
        :size="36"
        :play="coverPlay"
        :expand-testid="`track-card-expand-cover-${track.code ?? track.id}`"
        :failure-reason="failureReason"
        :failure-testid="`track-card-failed-${track.id}`"
      />
    </span>
    <div class="min-w-0 flex-1">
      <!-- The title IS the control, so it is the trigger rather than a button
           inside one — see OverflowTooltip's `triggerAs`. A <button> nested in
           the trigger would be hard-clipped mid-word with no ellipsis and no
           tooltip to recover the rest. -->
      <OverflowTooltip
        :deps="trackTitle"
        :trigger-as="titleTag"
        :trigger-class="compactTitleClass"
        :type="titleTag === 'button' ? 'button' : undefined"
        :data-testid="`track-card-open-track-${track.id}`"
        @click="titleTag === 'button' ? openTrackRecord() : undefined"
      >
        {{ trackTitle }}
        <template #tooltip>{{ trackTitle }}</template>
      </OverflowTooltip>
      <OverflowTooltip
        :deps="artistAlbumTooltip"
        trigger-class="text-muted-foreground text-xs leading-tight"
      >
        <span class="flex min-w-0 items-baseline">
          <component
            :is="artistTag"
            :type="artistTag === 'button' ? 'button' : undefined"
            :class="artistLineClass"
            :data-testid="`track-card-open-artist-${track.id}`"
            @click="artistTag === 'button' ? openArtistRecord() : undefined"
          >
            {{ artistLabel }}
          </component>
          <template v-if="albumLabel">
            <span class="shrink-0">&nbsp;—&nbsp;</span>
            <component
              :is="albumTag"
              :type="albumTag === 'button' ? 'button' : undefined"
              :class="albumLineClass"
              :data-testid="`track-card-open-album-${track.id}`"
              @click="albumTag === 'button' ? openAlbumRecord() : undefined"
            >
              {{ albumLabel }}
            </component>
          </template>
        </span>
        <template #tooltip>{{ artistAlbumTooltip }}</template>
      </OverflowTooltip>
    </div>
    <TrackVoteIndicator v-if="track.votes" :votes="track.votes" dense class="shrink-0" />
    <!-- Playlist order over the runtime, right-aligned. -->
    <div
      v-if="displayPosition != null || durationLabel"
      class="text-muted-foreground shrink-0 text-right font-mono text-[11px] leading-tight tabular-nums"
    >
      <div v-if="displayPosition != null" aria-hidden="true">{{ displayPosition }}</div>
      <div v-if="durationLabel">{{ durationLabel }}</div>
    </div>
  </div>

  <!-- Default: identity card with rating / genre / tags. -->
  <div
    v-else-if="density === 'default'"
    class="border-border/80 bg-card flex gap-3 rounded-xl border p-3 shadow-sm"
    :data-testid="`track-card-${track.code ?? track.id}`"
  >
    <!-- Stops its own clicks; see the compact branch above. -->
    <div class="flex w-14 shrink-0 flex-col" @click.stop>
      <CoverArtTile
        :src="track.cover_art_signed_url ?? null"
        :alt="`Cover art for ${trackTitle}`"
        :size="56"
        :play="coverPlay"
        :expand-testid="`track-card-expand-cover-${track.code ?? track.id}`"
        :failure-reason="failureReason"
        :failure-testid="`track-card-failed-${track.id}`"
      />
    </div>
    <div class="flex min-w-0 flex-1 flex-col">
      <!-- The title is the trigger itself — see the compact branch above. -->
      <OverflowTooltip
        :deps="trackTitle"
        :trigger-as="titleTag"
        :trigger-class="defaultTitleClass"
        :type="titleTag === 'button' ? 'button' : undefined"
        :data-testid="`track-card-open-track-${track.id}`"
        @click="titleTag === 'button' ? openTrackRecord() : undefined"
      >
        {{ trackTitle }}
        <template #tooltip>{{ trackTitle }}</template>
      </OverflowTooltip>
      <OverflowTooltip
        :deps="artistAlbumTooltip"
        trigger-class="text-foreground text-sm leading-snug"
      >
        <span class="flex min-w-0 items-baseline">
          <component
            :is="artistTag"
            :type="artistTag === 'button' ? 'button' : undefined"
            :class="artistLineClass"
            :data-testid="`track-card-open-artist-${track.id}`"
            @click="artistTag === 'button' ? openArtistRecord() : undefined"
          >
            {{ artistLabel }}
          </component>
          <template v-if="albumLabel">
            <span class="shrink-0">&nbsp;—&nbsp;</span>
            <component
              :is="albumTag"
              :type="albumTag === 'button' ? 'button' : undefined"
              :class="albumLineClass"
              :data-testid="`track-card-open-album-${track.id}`"
              @click="albumTag === 'button' ? openAlbumRecord() : undefined"
            >
              {{ albumLabel }}
            </component>
          </template>
        </span>
        <template #tooltip>{{ artistAlbumTooltip }}</template>
      </OverflowTooltip>
      <div class="mt-1.5 flex flex-wrap items-center gap-2">
        <!-- Always present: the rating, or a red NR so an unrated track is loud. -->
        <TrackRatingBadge :rating="track.rating || 'NR'" size="md" />
        <span v-if="track.genre_name" class="text-muted-foreground truncate text-xs leading-none">
          {{ track.genre_name }}
        </span>
        <!--
          Where this track's lyrics got to, after the genre. A link, not a label:
          the lyrics view used to be reachable only by adding the lyrics column
          to a table, and this opens the track straight onto it.
          Stops its own click so it still works inside a host that made the whole
          card one control (the play queue).
        -->
        <button
          v-if="track.lyrics_check_status"
          type="button"
          class="text-muted-foreground hover:text-primary cursor-pointer truncate text-xs leading-none underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
          :data-testid="`track-card-lyrics-status-${track.id}`"
          @click.stop="openLyricsView"
        >
          {{ track.lyrics_check_status.display_string }}
        </button>
        <TrackVoteIndicator v-if="track.votes" :votes="track.votes" />
      </div>
      <CardTags v-if="track.tags?.length" :tags="track.tags" />
    </div>
    <!-- Playlist order over the runtime, right-aligned — same pairing as the compact row. -->
    <div
      v-if="displayPosition != null || durationLabel"
      class="text-muted-foreground shrink-0 text-right font-mono text-[11px] leading-tight tabular-nums"
    >
      <div v-if="displayPosition != null" aria-hidden="true">{{ displayPosition }}</div>
      <div v-if="durationLabel">{{ durationLabel }}</div>
    </div>
  </div>

  <!-- Full: the original forensic card (code, playlist count, bitrate / ISRC / dates). -->
  <LiveTrackCard
    v-else
    card-class="p-3"
    :data-testid="`track-card-${track.code ?? track.id}`"
    :cover-url="track.cover_art_signed_url"
    :copyright="track.copyright"
    :duration="durationLabel"
    :title="trackTitle"
    :artist="track.artist_name"
    :album="track.album_name"
    :title-as-button="!hostHandlesClick"
    :title-tooltip="trackTitle"
    :title-testid="`track-card-open-track-${track.id}`"
    :artist-as-button="!hostHandlesClick && track.artist_id != null"
    :album-as-button="!hostHandlesClick && track.album_id != null"
    :artist-testid="`track-card-open-artist-${track.id}`"
    :album-testid="`track-card-open-album-${track.id}`"
    meta-column-padding="pr-0 @xs:pr-[4.5rem]"
    :cover-play="coverPlay"
    :cover-expand-testid="`track-card-expand-cover-${track.code ?? track.id}`"
    :cover-failure-reason="failureReason"
    :cover-failure-testid="`track-card-failed-${track.id}`"
    @title-click="openTrackRecord"
    @artist-click="openArtistRecord"
    @album-click="openAlbumRecord"
  >
    <template
      v-if="displayPosition != null || track.code || track.playlist_count != null"
      #corner
    >
      <div
        class="text-muted-foreground absolute top-3 right-3 z-[1] flex max-w-[calc(100%-6.5rem)] flex-col items-end gap-0.5 font-mono leading-none tabular-nums tracking-tight @xs:max-w-[12rem]"
      >
        <!-- Pivot-backed lists carry a playlist order; source-bucket lists send a
             null position and get no badge. -->
        <span
          v-if="displayPosition != null"
          class="bg-muted text-muted-foreground pointer-events-none rounded px-1.5 py-0.5 text-[11px] font-medium"
          aria-hidden="true"
        >
          #{{ displayPosition }}
        </span>
        <span
          v-if="track.code"
          class="pointer-events-none truncate text-[11px]"
          :title="track.code"
          aria-hidden="true"
        >
          {{ track.code }}
        </span>
        <LazyTooltip v-if="track.playlist_count != null" side="top" align="end">
          <button
            type="button"
            class="pointer-events-auto cursor-pointer text-[10px] underline-offset-2 hover:underline"
            :data-testid="`track-card-playlist-count-${track.id}`"
            @click.stop="openUsageInspector"
          >
            {{ track.playlist_count }}
          </button>
          <template #content>On playlists — click for details</template>
        </LazyTooltip>
      </div>
    </template>

    <template #badges>
      <div class="mt-1.5 flex flex-wrap items-center gap-2">
        <!-- Always present: the rating, or a red NR so an unrated track is loud. -->
        <TrackRatingBadge :rating="track.rating || 'NR'" size="md" />
        <span v-if="track.genre_name" class="text-muted-foreground truncate text-xs leading-none">
          {{ track.genre_name }}
        </span>
        <!--
          Where this track's lyrics got to, after the genre. A link, not a label:
          the lyrics view used to be reachable only by adding the lyrics column
          to a table, and this opens the track straight onto it.
          Stops its own click so it still works inside a host that made the whole
          card one control (the play queue).
        -->
        <button
          v-if="track.lyrics_check_status"
          type="button"
          class="text-muted-foreground hover:text-primary cursor-pointer truncate text-xs leading-none underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
          :data-testid="`track-card-lyrics-status-${track.id}`"
          @click.stop="openLyricsView"
        >
          {{ track.lyrics_check_status.display_string }}
        </button>
        <TrackVoteIndicator v-if="track.votes" :votes="track.votes" />
      </div>
    </template>

    <!-- Dates render date-only to keep this row on one line; the full timestamp
         lives in each tooltip. -->
    <template #meta>
      <div
        class="text-muted-foreground mt-auto flex flex-wrap items-baseline gap-x-2.5 gap-y-1 pt-1.5 font-mono text-[11px] leading-none tabular-nums"
      >
        <LazyTooltip v-if="track.bitrate != null">
          <span class="whitespace-nowrap">{{ track.bitrate }}k</span>
          <template #content>DB Bitrate</template>
        </LazyTooltip>
        <span v-if="track.isrc" class="whitespace-nowrap">ISRC {{ track.isrc }}</span>
        <LazyTooltip v-if="track.created_at" align="start" side="bottom">
          <span class="cursor-default whitespace-nowrap">{{
            formatUsMmDdYy(track.created_at)
          }}</span>
          <template #content>
            <TooltipRow :icon="CalendarPlus" label="Track created" class="tabular-nums">{{ formatUsMmDdYyTime12h(track.created_at) }}</TooltipRow>
          </template>
        </LazyTooltip>
        <LazyTooltip v-if="track.uploaded_date" align="start" side="bottom">
          <span class="cursor-default whitespace-nowrap">{{
            formatUsMmDdYy(track.uploaded_date)
          }}</span>
          <template #content>
            <TooltipRow :icon="Upload" label="Track uploaded" class="tabular-nums">{{ formatUsMmDdYyTime12h(track.uploaded_date) }}</TooltipRow>
          </template>
        </LazyTooltip>
      </div>
    </template>

    <!-- Tag chips, same colored catalog-resolved chips the search cards use. -->
    <template v-if="track.tags?.length" #footer>
      <CardTags :tags="track.tags" />
    </template>
  </LiveTrackCard>
</template>
